import styled from "styled-components";
import type { ReactNode } from "react";
import type { EditablePath } from "../editable/EditableContext";
import {
  TINT_GREEN,
  WASH,
  GREEN_ACCENT,
  BORDER_GREEN,
  INK,
  BORDER_SUBTLE,
  FONT_FIGTREE,
  SEMAPHORE,
  TEXT_VALUE,
  type SemaphoreToneWithNeutral,
} from "../tokens";
import { Ed, at } from "./EditableLeaf";
import { AddItem, RemoveItem } from "./EditableList";
import { ToneSelect, SEMAPHORE_OPTIONS } from "./EditableTone";

interface DetailRow {
  label: string;
  value: string;
  hint?: string;
  action?: string;
  badge?: string;
  active?: boolean;
  tone?: SemaphoreToneWithNeutral;
}

const ROW_TONES: Record<SemaphoreToneWithNeutral, string> = {
  green: SEMAPHORE.green.text,
  amber: SEMAPHORE.amber.text,
  red: SEMAPHORE.red.text,
  neutral: TEXT_VALUE,
};

const TableFrame = styled.div`
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 13px;
  overflow-x: auto;
  margin-bottom: 14px;
`;

const Table = styled.table<{ $variant: string }>`
  border-collapse: collapse;
  width: 100%;
  font-size: 12.5px;
  color: ${INK.soft};
  line-height: 1.5;

  th, td { padding: 12px 15px; vertical-align: baseline; text-align: left; }
  tr + tr { border-top: 1px solid ${BORDER_SUBTLE}; }
  th { width: ${(p) => p.$variant === "scenarios" ? "64px" : "150px"}; font-weight: 700; color: ${INK.strong}; }
  th { font-size: ${(p) => p.$variant === "scenarios" ? "12.5px" : "11px"}; }
  th { text-transform: ${(p) => p.$variant === "scenarios" ? "none" : "uppercase"}; }
  th { letter-spacing: ${(p) => p.$variant === "scenarios" ? "normal" : "0.03em"}; }
  tr[data-active="true"] { background: ${TINT_GREEN[200]}; }
  tr[data-active="true"] th { color: ${SEMAPHORE.green.text}; }
  td:last-child[data-action] { width: 150px; text-align: right; font-size: 11.5px; font-weight: 600; color: ${INK.strong}; }
  &[data-cost="true"] th { width: auto; font-size: 10.5px; font-weight: 600; color: ${INK.label}; }
  &[data-cost="true"] td { font-family: ${FONT_FIGTREE}; font-size: 14px; font-weight: 800; color: ${TEXT_VALUE}; text-align: right; white-space: nowrap; }
  &[data-kv="true"] th { width: auto; padding: 11px 15px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.04em; color: ${INK.label}; }
  &[data-kv="true"] td { padding: 11px 15px; font-family: ${FONT_FIGTREE}; font-size: 13px; font-weight: 600; color: ${TEXT_VALUE}; text-align: right; line-height: 1.4; }
`;

const RowHint = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  color: ${INK.faint};
`;

const ActiveBadge = styled.span`
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 8.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: ${SEMAPHORE.green.fill};
  color: white;
`;

export const ReportDetailTable = ({ rows, variant = "labels", path }: {
  rows: DetailRow[];
  variant?: "labels" | "scenarios" | "cost" | "keyValue";
  // Set by admin-interface's editor; absent in the client report.
  path?: EditablePath;
}) => (
  <TableFrame>
    <Table $variant={variant} data-cost={variant === "cost"} data-kv={variant === "keyValue"}>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} data-active={row.active === true}>
            <th scope="row">
              <Ed path={at(path, index, "label")} value={row.label} />
              {(row.hint || path) && <RowHint><Ed path={at(path, index, "hint")} value={row.hint} placeholder="Detalle" /></RowHint>}
            </th>
            {variant === "scenarios" && <td style={{ width: 1, padding: row.badge || path ? "12px 0" : 0 }}>{(row.badge || path) && <ActiveBadge><Ed path={at(path, index, "badge")} value={row.badge} placeholder="Etiqueta" /></ActiveBadge>}</td>}
            <td style={row.tone ? { color: ROW_TONES[row.tone] } : undefined}><Ed path={at(path, index, "value")} value={row.value} multiline /></td>
            {variant === "scenarios" && <td data-action><Ed path={at(path, index, "action")} value={row.action} /></td>}
            {path && <td style={{ width: 1, textAlign: "right", whiteSpace: "nowrap" }}>
              <ToneSelect path={at(path, index, "tone")} value={row.tone || "neutral"} options={SEMAPHORE_OPTIONS} />
              <RemoveItem path={path} index={index} count={rows.length} />
            </td>}
          </tr>
        ))}
      </tbody>
    </Table>
    <AddItem path={path} count={rows.length} />
  </TableFrame>
);

const Checklist = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  margin: 0;
  list-style: none;

  li { display: flex; gap: 13px; align-items: flex-start; }
  strong { display: block; font-family: ${FONT_FIGTREE}; font-size: 12.5px; font-weight: 700; color: ${INK.strong}; margin-bottom: 2px; }
  p { margin: 0; font-size: 12.5px; color: ${INK.soft}; line-height: 1.5; }
`;

const ChecklistNumber = styled.span`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: ${WASH};
  border: 1px solid ${BORDER_GREEN};
  color: ${GREEN_ACCENT};
  font-family: ${FONT_FIGTREE};
  font-size: 10.5px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ReportChecklist = ({ items, renderText = (text) => text, path }: {
  items: Array<{ number: string; title: string; text: string }>;
  renderText?: (text: string) => ReactNode;
  // Set by admin-interface's editor; absent in the client report.
  path?: EditablePath;
}) => (
  <Checklist>{items.map((item, index) => (
    <li key={index}>
      <ChecklistNumber><Ed path={at(path, index, "number")} value={item.number} /></ChecklistNumber>
      <div>
        <strong><Ed path={at(path, index, "title")} value={item.title} /></strong>
        <p><Ed path={at(path, index, "text")} value={item.text} multiline render={renderText} /></p>
      </div>
      <RemoveItem path={path} index={index} count={items.length} title="Eliminar punto" />
    </li>
  ))}<AddItem path={path} count={items.length} label="+ punto" /></Checklist>
);
